import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { authClient } from "@/lib/auth";

interface GuestModeContextType {
  isGuest: boolean;
  enterGuestMode: () => void;
  exitGuestMode: () => void;
  showRegisterPrompt: boolean;
  setShowRegisterPrompt: (show: boolean) => void;
  requireAuth: (action?: string) => boolean;
  /** What the guest was trying to do when the prompt opened (e.g.
   *  "complete-habit"). Lets RegisterPrompt name the thing they're about to
   *  save instead of asking generically — see RegisterPrompt.tsx. */
  registerPromptAction: string | null;
}

const GuestModeContext = createContext<GuestModeContextType>({
  isGuest: false,
  enterGuestMode: () => {},
  exitGuestMode: () => {},
  showRegisterPrompt: false,
  setShowRegisterPrompt: () => {},
  requireAuth: () => true,
  registerPromptAction: null,
});

export function GuestModeProvider({ children }: { children: ReactNode }) {
  const [isGuest, setIsGuest] = useState(() => {
    return sessionStorage.getItem("trybeup_guest") === "true";
  });
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [registerPromptAction, setRegisterPromptAction] = useState<string | null>(null);

  const enterGuestMode = useCallback(() => {
    sessionStorage.setItem("trybeup_guest", "true");
    setIsGuest(true);
  }, []);

  const exitGuestMode = useCallback(() => {
    sessionStorage.removeItem("trybeup_guest");
    setIsGuest(false);
  }, []);

  // Keep guest mode mutually exclusive with authentication. The login /
  // signup flows (including Google OAuth, which lands on `/` without
  // hitting LoginPage) don't explicitly call exitGuestMode, so a stale
  // sessionStorage flag would otherwise cause MainApp to render the
  // "Preview mode" banner + "Sign Up Free" header button on top of a
  // personalized, authenticated page. Clear the flag as soon as we see
  // a real session — both on mount (covers page reload while signed in)
  // and on any future SIGNED_IN event.
  useEffect(() => {
    authClient.getSession().then(({ data }) => {
      if (data.session) {
        sessionStorage.removeItem("trybeup_guest");
        setIsGuest(false);
      }
    }).catch(() => {});

    const { data: listener } = authClient.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        sessionStorage.removeItem("trybeup_guest");
        setIsGuest(false);
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // `action` was accepted and then thrown away, so every guest wall showed
  // the same generic pitch no matter what the person was reaching for
  // (conversion audit 2026-08-10). Keeping it lets the prompt say "save the
  // habit you just checked off" — the difference between an interruption
  // and an offer.
  const requireAuth = useCallback((action?: string) => {
    if (isGuest) {
      setRegisterPromptAction(action ?? null);
      setShowRegisterPrompt(true);
      return false;
    }
    return true;
  }, [isGuest]);

  // Drop the action when the prompt closes, so a future caller that opens the
  // dialog with a bare setShowRegisterPrompt(true) can't inherit the last
  // wall's copy — e.g. "Save that check" on a screen with no checkbox
  // (review 2026-08-10).
  useEffect(() => {
    if (!showRegisterPrompt) setRegisterPromptAction(null);
  }, [showRegisterPrompt]);

  return (
    <GuestModeContext.Provider value={{ isGuest, enterGuestMode, exitGuestMode, showRegisterPrompt, setShowRegisterPrompt, requireAuth, registerPromptAction }}>
      {children}
    </GuestModeContext.Provider>
  );
}

export function useGuestMode() {
  return useContext(GuestModeContext);
}
